import { useState } from "react";
import { useIntake } from "../../../contexts/IntakeContext";
import { Badge } from "../../../design/badge";
import { Divider } from "../../../design/divider";
import { Heading, Subheading } from "../../../design/heading";
import { Pie, PieChart, ResponsiveContainer, Cell } from "recharts";
import { Select } from "../../../design/select";

const timeframes = ["Today", "Week", "Month", "Year", "Total"] as const;
type Timeframe = (typeof timeframes)[number];

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#a4de6c"];

const IntakeStatistics = () => {
  const { intakes } = useIntake();
  const [timeframe, setTimeframe] = useState<Timeframe>("Week");

  const now = new Date();
  const filteredIntakes = intakes.filter((intake) => {
    const createdAt = new Date(intake.createdAt); // Handle both Firestore Timestamp and Date object
    if (isNaN(createdAt.getTime())) return false;

    switch (timeframe) {
      case "Today":
        const dayAgo = new Date(now);
        dayAgo.setHours(now.getHours() - 24);
        return createdAt >= dayAgo;
      case "Week":
        const weekAgo = new Date(now);
        weekAgo.setDate(now.getDate() - 7);
        return createdAt >= weekAgo;
      case "Month":
        const monthAgo = new Date(now);
        monthAgo.setMonth(now.getMonth() - 1);
        return createdAt >= monthAgo;
      case "Year":
        const yearAgo = new Date(now);
        yearAgo.setFullYear(now.getFullYear() - 1);
        return createdAt >= yearAgo;
      case "Total":
      default:
        return true;
    }
  });

  const totalIntakes = filteredIntakes.length;
  const qualifiedIntakes = filteredIntakes.filter(
    (i) => i.rating?.proceedWithMatter,
  ).length;
  const unqualifiedIntakes = totalIntakes - qualifiedIntakes;

  const intakesByStatus = filteredIntakes.reduce(
    (acc: Record<string, number>, intake) => {
      const status = intake.status || "unknown";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {},
  );

  const pieData = Object.entries(intakesByStatus).map(([status, count]) => ({
    name: status,
    value: count,
  }));

  return (
    <div className="flex flex-col w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div />
        <div>
          <Select
            onChange={(e) => setTimeframe(e.target.value as Timeframe)}
            value={timeframe}
          >
            {timeframes.map((option) => (
              <option key={option} value={option} className="p-2">
                {option}
              </option>
            ))}
          </Select>
        </div>
      </div>
      {/* Stats Row */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="p-4 border rounded-lg bg-gray-50 dark:bg-zinc-900">
          <Subheading>Total Intakes</Subheading>
          <Heading>{totalIntakes}</Heading>
        </div>
        <div className="p-4 border rounded-lg bg-gray-50 dark:bg-zinc-900">
          <Subheading>Qualified</Subheading>
          <Heading>{qualifiedIntakes}</Heading>
        </div>
        <div className="p-4 border rounded-lg bg-gray-50 dark:bg-zinc-900">
          <Subheading>Unqualified</Subheading>
          <Heading>{unqualifiedIntakes}</Heading>
        </div>
      </div>

      {/* Chart + Statuses Row */}
      <div className="grid grid-cols-1 gap-6 mt-4 md:grid-cols-2">
        <div className="p-4 border rounded-lg h-72 bg-gray-50 dark:bg-zinc-900">
          <Subheading>Intakes by Status</Subheading>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                dataKey="value"
                label={({ name, percent }) =>
                  `${name} (${(percent * 100).toFixed(0)}%)`
                }
              >
                {pieData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="p-4 border rounded-lg bg-gray-50 dark:bg-zinc-900">
          <Subheading>Status Breakdown</Subheading>
          <ul className="flex flex-wrap gap-2 mt-2">
            {Object.entries(intakesByStatus).map(([status, count]) => (
              <Badge key={status}>
                {status}: {count}
              </Badge>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default IntakeStatistics;
